// src/pages/Contact.jsx - Modified to use server-side OG generation
// Social media scrapers get OG tags from API, not from client-side Helmet

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FiSend,
  FiMail,
  FiMapPin,
  FiPhone,
  FiMessageCircle,
  FiInstagram,
  FiTwitter,
  FiLinkedin,
  FiDribbble,
  FiGithub,
  FiCheckCircle,
  FiAlertCircle,
  FiArrowRight,
  FiUser,
  FiEdit2,
  FiSmile,
  FiLoader,
  FiInfo,
  FiCheck,
  FiX,
  FiShield,
  FiThumbsUp,
  FiAward,
  FiZap,
  FiTarget,
  FiGlobe,
  FiPlus,
  FiMinus,
  FiType,
  FiCode,
  FiEye,
  FiHeart,
  FiStar,
  FiTrendingUp,
  FiShoppingBag,
  FiBookOpen,
  FiMusic,
  FiVideo,
  FiCamera,
  FiPenTool,
  FiLayout,
  FiSmartphone,
  FiCloud,
  FiDatabase,
  FiLock,
  FiUsers,
  FiBriefcase,
  FiGrid,
  FiBookOpen as FiBookOpenIcon,
  FiExternalLink,
  FiClock,
  FiAlertTriangle,
} from 'react-icons/fi';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { TfiPinterest } from 'react-icons/tfi';
import { FaBehance } from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Autoplay,
  Pagination,
  Navigation,
  EffectCoverflow,
} from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-coverflow';

import contactService from '../services/contactService';
import {
  useEmailValidation,
  EmailValidationInput,
} from '../utils/emailValidation.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { FiverrCard } from '../components/FiverrCard';
// ✅ REMOVED: ogImageService - No client-side OG generation
// import { ogImageService } from '../services/ogImageService';

// =============================================
// GLASSY DIVIDER
// =============================================
const GlassyDivider = () => {
  return (
    <div className="relative py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="relative h-px w-full overflow-hidden">
          <motion.div
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute inset-0 w-1/3"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--text), transparent)',
              opacity: 0.08,
            }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================
// GLASSY CARD - with soft tiny border
// =============================================
const GlassyCard = ({ children, className = '' }) => {
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-xl ${className}`}
      style={{
        backgroundColor: 'rgba(var(--bg-rgb), 0.3)',
        border: '1px solid rgba(var(--text-rgb), 0.06)',
        borderRadius: '16px',
        boxShadow:
          '0 4px 24px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/3 via-transparent to-transparent" />
        <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
      </div>
      {children}
    </div>
  );
};

// =============================================
// LIQUID GRADIENT HERO
// =============================================
const ContactHero = () => {
  return (
    <div className="relative mb-8 overflow-hidden rounded-3xl">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:from-blue-500/10 dark:via-transparent dark:to-purple-500/10" />

      <motion.div
        animate={{
          x: ['-20%', '15%', '-5%', '20%', '-20%'],
          y: ['-15%', '10%', '-10%', '15%', '-15%'],
          scale: [1, 1.3, 0.9, 1.2, 1],
          borderRadius: [
            '60% 40% 30% 70%',
            '40% 60% 70% 30%',
            '30% 70% 60% 40%',
            '70% 30% 40% 60%',
            '60% 40% 30% 70%',
          ],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-5 left-5 w-72 h-72 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #3b82f6 0%, #60a5fa 40%, transparent 70%)',
          opacity: 0.1,
        }}
      />

      <motion.div
        animate={{
          x: ['15%', '-15%', '10%', '-10%', '15%'],
          y: ['15%', '-10%', '20%', '-15%', '15%'],
          scale: [1.2, 0.9, 1.4, 0.8, 1.2],
          borderRadius: [
            '40% 60% 70% 30%',
            '60% 40% 30% 70%',
            '70% 30% 40% 60%',
            '30% 70% 60% 40%',
            '40% 60% 70% 30%',
          ],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-5 right-5 w-80 h-80 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #8b5cf6 0%, #a78bfa 40%, transparent 70%)',
          opacity: 0.08,
        }}
      />

      <motion.div
        animate={{
          x: ['-5%', '10%', '-10%', '5%', '-5%'],
          y: ['10%', '-15%', '5%', '-10%', '10%'],
          scale: [1, 1.2, 0.8, 1.3, 1],
          borderRadius: [
            '50% 30% 60% 40%',
            '30% 60% 40% 70%',
            '60% 40% 70% 30%',
            '40% 70% 30% 60%',
            '50% 30% 60% 40%',
          ],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #06b6d4 0%, #22d3ee 40%, transparent 70%)',
          opacity: 0.06,
        }}
      />

      <motion.div
        animate={{
          x: ['10%', '-20%', '15%', '-5%', '10%'],
          y: ['-5%', '15%', '-15%', '10%', '-5%'],
          scale: [0.8, 1.4, 0.9, 1.1, 0.8],
          borderRadius: [
            '70% 30% 50% 50%',
            '30% 70% 50% 50%',
            '50% 50% 70% 30%',
            '50% 50% 30% 70%',
            '70% 30% 50% 50%',
          ],
        }}
        transition={{
          duration: 22,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-20 left-20 w-60 h-60 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #ec4899 0%, #f472b6 40%, transparent 70%)',
          opacity: 0.05,
        }}
      />

      <div className="relative px-6 py-10 md:py-14 lg:py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 text-sm backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.5)',
            border: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <FiBookOpenIcon className="w-4 h-4" style={{ color: '#3b82f6' }} />
          <span style={{ color: 'var(--muted)' }}>Let's Connect</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3 tracking-tight leading-[1.1]"
          style={{ color: 'var(--text)' }}
        >
          Start a
          <span className="block" style={{ color: 'var(--muted)' }}>
            conversation
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-sm md:text-base max-w-2xl mx-auto leading-relaxed"
          style={{ color: 'var(--muted)', opacity: 0.8 }}
        >
          Ready to bring your ideas to life? I'm just a message away. Let's
          create something extraordinary together.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-6 flex items-center justify-center gap-3"
        >
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <span
            className="text-xs tracking-widest uppercase"
            style={{ color: 'var(--muted)', opacity: 0.3 }}
          >
            Contact
          </span>
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
        </motion.div>
      </div>
    </div>
  );
};

// =============================================
// ANIMATED TRUSTED COMPANIES - Black & White Logos
// =============================================
const AnimatedTrustedCompanies = () => {
  const companies = [
    { name: 'Google', logo: '/logos/google.svg', color: '#4285F4' },
    { name: 'Microsoft', logo: '/logos/microsoft.svg', color: '#00A4EF' },
    { name: 'Adobe', logo: '/logos/adobe.svg', color: '#FF0000' },
    { name: 'Spotify', logo: '/logos/spotify.svg', color: '#1DB954' },
    { name: 'Netflix', logo: '/logos/netflix.svg', color: '#E50914' },
    { name: 'Amazon', logo: '/logos/amazon.svg', color: '#FF9900' },
    { name: 'Apple', logo: '/logos/apple.svg', color: '#A2AAAD' },
    { name: 'Meta', logo: '/logos/meta.svg', color: '#0064E1' },
    { name: 'Twitter', logo: '/logos/twitter.svg', color: '#1DA1F2' },
    { name: 'LinkedIn', logo: '/logos/linkedin.svg', color: '#0A66C2' },
  ];

  return (
    <div className="relative overflow-hidden py-8">
      {/* Minimal gradient glow effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/4 w-80 h-80 rounded-full blur-[100px] opacity-10"
          style={{
            background:
              'radial-gradient(circle, var(--text) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute top-1/2 right-1/4 w-80 h-80 rounded-full blur-[100px] opacity-10"
          style={{
            background:
              'radial-gradient(circle, var(--text) 0%, transparent 70%)',
          }}
        />
      </div>

      <div className="relative z-10">
        <Swiper
          modules={[Autoplay, Pagination, EffectCoverflow]}
          spaceBetween={20}
          slidesPerView={2}
          centeredSlides={false}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          loop={true}
          speed={1000}
          breakpoints={{
            480: { slidesPerView: 3, spaceBetween: 16 },
            640: { slidesPerView: 4, spaceBetween: 20 },
            768: { slidesPerView: 5, spaceBetween: 24 },
            1024: { slidesPerView: 6, spaceBetween: 28 },
            1280: { slidesPerView: 7, spaceBetween: 32 },
          }}
          className="trusted-companies-swiper"
        >
          {companies.map((company, idx) => (
            <SwiperSlide key={idx}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: idx * 0.05,
                  duration: 0.5,
                  type: 'spring',
                  stiffness: 200,
                }}
                viewport={{ once: true }}
                className="group relative"
                whileHover={{ y: -4 }}
              >
                <GlassyCard className="p-5 md:p-6 text-center rounded-2xl transition-all duration-500 cursor-pointer min-h-[100px] flex items-center justify-center">
                  {/* Minimal hover gradient */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: `linear-gradient(135deg, ${company.color}06, transparent 70%)`,
                    }}
                  />

                  <div className="relative z-10 w-full">
                    {/* Black & White Logo */}
                    <motion.div
                      className="flex items-center justify-center mb-2"
                      animate={{
                        scale: [1, 1.02, 1],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: idx * 0.1,
                      }}
                    >
                      <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center grayscale group-hover:grayscale-0 transition-all duration-500">
                        <img
                          src={company.logo}
                          alt={company.name}
                          className="w-full h-full object-contain filter brightness-75 dark:brightness-100 group-hover:brightness-100 transition-all duration-500"
                        />
                      </div>
                    </motion.div>

                    {/* Company name */}
                    <motion.h3
                      className="font-medium text-xs md:text-sm transition-colors duration-300"
                      style={{ color: 'var(--muted)' }}
                      whileHover={{ color: company.color }}
                    >
                      {company.name}
                    </motion.h3>

                    {/* Minimal underline */}
                    <motion.div
                      className="h-px mx-auto mt-1.5 rounded-full transition-all duration-500"
                      style={{ backgroundColor: company.color }}
                      initial={{ width: 0, opacity: 0 }}
                      whileInView={{ width: '20%', opacity: 0.3 }}
                      whileHover={{ width: '40%', opacity: 0.6 }}
                      transition={{ duration: 0.5, delay: idx * 0.05 }}
                      viewport={{ once: true }}
                    />
                  </div>
                </GlassyCard>
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Pagination */}
        <div className="flex justify-center gap-2 mt-6">
          {companies.slice(0, 5).map((_, idx) => (
            <motion.button
              key={idx}
              className="w-1.5 h-1.5 rounded-full transition-all duration-300"
              style={{ backgroundColor: 'var(--border)' }}
              whileHover={{ scale: 1.8, backgroundColor: 'var(--text)' }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

function Contact() {
  // ==================== FORM STATE ====================
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    customSubject: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [validationProgress, setValidationProgress] = useState(0);
  const [useCustomSubject, setUseCustomSubject] = useState(false);
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);
  const [showSpamAlert, setShowSpamAlert] = useState(true);

  // Email validation state
  const [emailValidation, setEmailValidation] = useState({
    isValid: false,
    errorMessage: null,
    suggestion: null,
    qualityScore: 0,
    isValidating: false,
  });

  // ==================== ANIMATION ON SCROLL ====================
  const [animatedSections, setAnimatedSections] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -100px 0px' },
    );

    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // ==================== UPDATED SUBJECT OPTIONS ====================
  const subjectOptions = [
    {
      value: 'brand-identity',
      label: '🎨 Brand Identity Design',
      description:
        'Complete brand identity, brand guidelines, visual identity system',
      icon: FiStar,
      category: 'Design',
    },
    {
      value: 'logo-design',
      label: '🏷️ Logo Design',
      description: 'Custom logo design, brand mark, icon design',
      icon: FiPenTool,
      category: 'Design',
    },
    {
      value: 'product-visual',
      label: '📦 Product Visual Design',
      description:
        'Packaging design, product presentation, visual merchandising',
      icon: FiShoppingBag,
      category: 'Design',
    },
    {
      value: 'image-tracing',
      label: '🔍 Image Tracing',
      description: 'Vector tracing, image conversion, logo vectorization',
      icon: FiEye,
      category: 'Design',
    },
    {
      value: 'general',
      label: '💬 General Inquiry',
      description: 'Just say hello or ask questions',
      icon: FiSmile,
      category: 'General',
    },
  ];

  // Group subjects by category
  const groupedSubjects = subjectOptions.reduce((acc, option) => {
    if (!acc[option.category]) {
      acc[option.category] = [];
    }
    acc[option.category].push(option);
    return acc;
  }, {});

  // ==================== ADVANCED VALIDATION RULES ====================
  const validationRules = {
    name: {
      required: true,
      minLength: 2,
      maxLength: 100,
      pattern: /^[a-zA-Z\s]+$/,
      patternMessage: 'Only letters and spaces allowed',
      requiredMessage: 'Please tell me your name',
      minLengthMessage: 'Name must be at least 2 characters',
      maxLengthMessage: 'Name is too long (max 100 characters)',
    },
    subject: {
      required: true,
      requiredMessage: 'Please select a subject or write your own',
    },
    customSubject: {
      required: false,
      minLength: 3,
      maxLength: 100,
      minLengthMessage: 'Subject must be at least 3 characters',
      maxLengthMessage: 'Subject is too long (max 100 characters)',
    },
    message: {
      required: true,
      minLength: 10,
      maxLength: 5000,
      minLengthMessage: 'Please provide more details (minimum 10 characters)',
      maxLengthMessage: 'Message is too long (maximum 5000 characters)',
      requiredMessage: 'Please write your message',
    },
  };

  const validateField = (fieldName, value) => {
    const rule = validationRules[fieldName];
    if (!rule) return '';

    if (rule.required && !value?.trim()) {
      return rule.requiredMessage || `${fieldName} is required`;
    }

    if (rule.minLength && value?.trim()?.length < rule.minLength) {
      return (
        rule.minLengthMessage ||
        `${fieldName} must be at least ${rule.minLength} characters`
      );
    }

    if (rule.maxLength && value?.trim()?.length > rule.maxLength) {
      return (
        rule.maxLengthMessage ||
        `${fieldName} cannot exceed ${rule.maxLength} characters`
      );
    }

    if (rule.pattern && !rule.pattern.test(value?.trim())) {
      return rule.patternMessage || `Invalid ${fieldName} format`;
    }

    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    let progress = 0;
    let fieldsValidated = 0;
    const totalFields = 4;

    const nameError = validateField('name', formData.name);
    if (nameError) {
      newErrors.name = nameError;
    } else {
      fieldsValidated++;
    }

    if (!emailValidation.isValid) {
      newErrors.email =
        emailValidation.errorMessage || 'Please enter a valid email address';
    } else {
      fieldsValidated++;
    }

    if (useCustomSubject) {
      const customSubjectError = validateField(
        'customSubject',
        formData.customSubject,
      );
      if (customSubjectError) {
        newErrors.customSubject = customSubjectError;
      } else {
        fieldsValidated++;
      }
    } else {
      if (!formData.subject) {
        newErrors.subject = validationRules.subject.requiredMessage;
      } else {
        fieldsValidated++;
      }
    }

    const messageError = validateField('message', formData.message);
    if (messageError) {
      newErrors.message = messageError;
    } else {
      fieldsValidated++;
    }

    progress = (fieldsValidated / totalFields) * 100;
    setValidationProgress(progress);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      validateForm();
    }
  }, [formData, touched, useCustomSubject, emailValidation]);

  const handleBlur = fieldName => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    if (fieldName !== 'email') {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        setErrors(prev => ({ ...prev, [fieldName]: error }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    }
  };

  const getFieldStatus = fieldName => {
    if (!touched[fieldName]) return null;
    return errors[fieldName] ? 'error' : 'success';
  };

  // ==================== INTERACTIVE BACKGROUND ====================
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let particles = [];
    let mouseX = 0;
    let mouseY = 0;
    let animationId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const initParticles = () => {
      particles = [];
      for (let i = 0; i < 40; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 1.5 + 0.5,
          speedX: (Math.random() - 0.5) * 0.2,
          speedY: (Math.random() - 0.5) * 0.2,
          opacity: Math.random() * 0.15,
        });
      }
    };

    const drawParticles = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const isDark = document.documentElement.classList.contains('dark');

      particles.forEach(particle => {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = isDark
          ? `rgba(255,255,255,${particle.opacity})`
          : `rgba(0,0,0,${particle.opacity})`;
        ctx.fill();

        particle.x += particle.speedX;
        particle.y += particle.speedY;

        if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
      });

      animationId = requestAnimationFrame(drawParticles);
    };

    resizeCanvas();
    initParticles();
    drawParticles();

    window.addEventListener('resize', () => {
      resizeCanvas();
      initParticles();
    });

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  // ==================== FORM HANDLERS ====================
  const handleSubmit = async e => {
    e.preventDefault();

    const allTouched = {};
    Object.keys(validationRules).forEach(field => {
      if (field !== 'customSubject' || useCustomSubject) {
        allTouched[field] = true;
      }
    });
    setTouched(allTouched);
    setTouched(prev => ({ ...prev, email: true }));

    const isValid = validateForm();

    if (isValid && emailValidation.isValid) {
      setIsSubmitting(true);
      setSubmitError(null);

      const finalSubject = useCustomSubject
        ? formData.customSubject
        : subjectOptions.find(opt => opt.value === formData.subject)?.label ||
          formData.subject;

      const submitData = {
        name: formData.name,
        email: formData.email,
        subject: finalSubject,
        message: formData.message,
      };

      try {
        await contactService.submitMessage(submitData);
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          customSubject: '',
          message: '',
        });
        setTouched({});
        setErrors({});
        setValidationProgress(0);
        setUseCustomSubject(false);
        setEmailValidation({
          isValid: false,
          errorMessage: null,
          suggestion: null,
          qualityScore: 0,
          isValidating: false,
        });
        setTimeout(() => setIsSubmitted(false), 5000);
      } catch (error) {
        console.error('Error submitting message:', error);
        setSubmitError(
          error.message || 'Failed to send message. Please try again later.',
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleEmailValidationChange = (isValid, validationResult) => {
    setEmailValidation({
      isValid: isValid,
      errorMessage: validationResult?.reason || null,
      suggestion: validationResult?.suggestions || null,
      qualityScore: validationResult?.score || 0,
      isValidating: false,
    });
  };

  // ==================== DATA ====================
  const contactInfo = [
    {
      icon: FiMail,
      title: 'Email',
      value: 'wdhejbul@gmail.com',
      link: 'mailto:wdhejbul@gmail.com',
    },
    {
      icon: FiMapPin,
      title: 'Location',
      value: 'Dhaka, Bangladesh',
      link: null,
    },
    {
      icon: FiPhone,
      title: 'Phone',
      value: '+880 1884-521448',
      link: 'tel:+8801884521448',
    },
    {
      icon: FiGlobe,
      title: 'Available',
      value: 'Worldwide Remote',
      link: null,
    },
  ];

  // =============================================
  // SOCIAL LINKS - Matching Footer Exactly
  // =============================================
  const socialLinks = [
    {
      name: 'Facebook',
      icon: FiInstagram, // Using Instagram icon since FiFacebook is available
      link: 'https://www.facebook.com/wdhejbul',
      color: '#1877F2',
    },
    {
      name: 'Instagram',
      icon: FiInstagram,
      link: 'https://www.instagram.com/wdhejbul/',
      color: '#E4405F',
    },
    {
      name: 'LinkedIn',
      icon: FiLinkedin,
      link: 'https://www.linkedin.com/in/hejbul-rahman-tasin-127350265',
      color: '#0A66C2',
    },
    {
      name: 'Twitter',
      icon: FiTwitter,
      link: 'https://x.com/wdhejbul',
      color: '#1DA1F2',
    },
    {
      name: 'Pinterest',
      icon: TfiPinterest,
      link: 'https://www.pinterest.com/wdhejbul',
      color: '#E60023',
    },
    {
      name: 'Behance',
      icon: FaBehance,
      link: 'https://www.behance.net/wdhejbul',
      color: '#1769FF',
    },
    {
      name: 'GitHub',
      icon: FiGithub,
      link: 'https://github.com/wdhejbul',
      color: '#6e5494',
    },
  ];

  const faqs = [
    {
      q: "What's your response time?",
      a: 'Usually within 24 hours on business days. I personally respond to every message.',
    },
    {
      q: 'Do you take international projects?',
      a: 'Absolutely! I work with clients worldwide. Time zone differences are never a problem.',
    },
    {
      q: "What's your design process?",
      a: 'Discovery → Concept → Design → Delivery. I ensure clear communication at every stage.',
    },
    {
      q: 'How do you protect my privacy?',
      a: 'Your information is never shared with third parties. All communications are confidential.',
    },
  ];

  const benefits = [
    {
      icon: FiZap,
      title: 'Fast Response',
      text: 'Get back to you within 24 hours',
    },
    {
      icon: FiShield,
      title: '100% Privacy',
      text: 'Your data is safe with me',
    },
    {
      icon: FiThumbsUp,
      title: 'Quality Guarantee',
      text: 'Satisfaction guaranteed',
    },
    {
      icon: FiGlobe,
      title: 'Global Client',
      text: 'Work with clients worldwide',
    },
  ];

  const isFormValid =
    Object.keys(errors).length === 0 &&
    formData.name.trim() &&
    emailValidation.isValid &&
    (useCustomSubject ? formData.customSubject?.trim() : formData.subject) &&
    formData.message.trim();

  return (
    <>
      {/*
        ✅ HELMET - Only basic page info
        ❌ NO OG TAGS - Server handles OG generation via API
        Social media scrapers get OG from /api/social-preview?page=contact
      */}
      <Helmet>
        <title>Contact | WD Hajbul - Get in Touch</title>
        <meta
          name="description"
          content="Let's discuss your next project. Contact WD Hajbul for logo design, branding, and product visuals."
        />
        <meta
          name="keywords"
          content="contact, WD Hajbul, designer, brand designer, product designer, hire, collaborate, project"
        />
        <link rel="canonical" href="https://wdhejbul.com/contact" />
        <meta name="robots" content="index, follow" />

        {/* Schema for Contact page */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Contact WD Hajbul',
            description:
              "Let's discuss your next project. Contact WD Hajbul for logo design, branding, and product visuals.",
            url: 'https://wdhejbul.com/contact',
            mainEntity: {
              '@type': 'Person',
              name: 'WD Hajbul',
              email: 'wdhejbul@gmail.com',
              telephone: '+8801884521448',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Dhaka',
                addressCountry: 'Bangladesh',
              },
              sameAs: [
                'https://www.linkedin.com/in/hejbul-rahman-tasin-127350265',
                'https://www.behance.net/wdhejbul',
                'https://github.com/wdhejbul',
                'https://www.instagram.com/wdhejbul/',
              ],
            },
          })}
        </script>
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* Interactive Background */}
        <canvas
          ref={canvasRef}
          className="fixed top-0 left-0 w-full h-full pointer-events-none opacity-10"
        />

        {/* ==================== HERO SECTION ==================== */}
        <section className="pt-32 px-4 max-w-7xl mx-auto max-sm:p-5 max-sm:pt-24">
          <ContactHero />
        </section>

        <GlassyDivider />

        {/* ==================== CONTACT INFO - MODERN GRID WITH ANIMATED ICONS ==================== */}
        <section className="animate-on-scroll py-16 px-4 max-w-6xl mx-auto transition-all duration-700 delay-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactInfo.map((info, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  delay: idx * 0.08,
                  duration: 0.5,
                  ease: 'easeOut',
                }}
                viewport={{ once: true }}
                className="group relative"
              >
                <div
                  className="relative p-6 rounded-2xl transition-all duration-500 overflow-hidden"
                  style={{
                    backgroundColor: 'var(--bg)',
                    border: '1px solid var(--border)',
                  }}
                >
                  {/* Animated gradient background on hover */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                    style={{
                      background: `radial-gradient(circle at 30% 30%, var(--text) 0%, transparent 70%)`,
                      opacity: 0.03,
                    }}
                  />

                  {/* Floating particles on hover */}
                  <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1 h-1 rounded-full"
                        style={{
                          backgroundColor: 'var(--text)',
                          opacity: 0.15,
                        }}
                        initial={{
                          x: Math.random() * 100 + '%',
                          y: Math.random() * 100 + '%',
                          scale: 0,
                        }}
                        whileHover={{
                          x: Math.random() * 100 + '%',
                          y: Math.random() * 100 + '%',
                          scale: [0, 1, 0],
                          opacity: [0, 0.4, 0],
                        }}
                        transition={{
                          duration: 2 + Math.random() * 2,
                          repeat: Infinity,
                          delay: Math.random() * 2,
                        }}
                      />
                    ))}
                  </div>

                  <div className="relative z-10 flex items-start gap-4">
                    {/* Animated Icon Container */}
                    <motion.div
                      className="relative flex-shrink-0"
                      whileHover={{ rotate: [0, -10, 10, -5, 5, 0] }}
                      transition={{ duration: 0.6 }}
                    >
                      {/* Pulsing ring */}
                      <motion.div
                        className="absolute inset-0 rounded-xl"
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.3, 0, 0.3],
                        }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                          delay: idx * 0.2,
                        }}
                        style={{
                          border: '2px solid var(--text)',
                          opacity: 0.1,
                        }}
                      />
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg"
                        style={{
                          backgroundColor: 'var(--border)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                        }}
                      >
                        <info.icon
                          className="w-5 h-5 transition-all duration-300 group-hover:rotate-12"
                          style={{ color: 'var(--text)' }}
                        />
                      </div>
                    </motion.div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <motion.h3
                        className="text-xs font-medium uppercase tracking-wider mb-1.5"
                        style={{ color: 'var(--muted)' }}
                      >
                        {info.title}
                      </motion.h3>
                      {info.link ? (
                        <motion.a
                          href={info.link}
                          className="text-base font-semibold hover:opacity-70 transition-opacity block truncate"
                          style={{ color: 'var(--text)' }}
                          whileHover={{ x: 4 }}
                        >
                          {info.value}
                        </motion.a>
                      ) : (
                        <motion.p
                          className="text-base font-semibold truncate"
                          style={{ color: 'var(--text)' }}
                        >
                          {info.value}
                        </motion.p>
                      )}
                      {/* Decorative line */}
                      <motion.div
                        className="w-8 h-px mt-2 rounded-full transition-all duration-300 group-hover:w-12"
                        style={{
                          backgroundColor: 'var(--border)',
                        }}
                      />
                    </div>
                  </div>

                  {/* Corner accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden pointer-events-none">
                    <div
                      className="absolute top-0 right-0 w-8 h-8 -translate-y-1/2 translate-x-1/2 rotate-45"
                      style={{
                        backgroundColor: 'var(--border)',
                        opacity: 0.3,
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <GlassyDivider />

        {/* ==================== BENEFITS - MODERN CARDS WITH ANIMATED PROGRESS ==================== */}
        <section className="animate-on-scroll py-16 px-4 max-w-6xl mx-auto transition-all duration-700 delay-150">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: idx * 0.1,
                  duration: 0.5,
                  type: 'spring',
                  stiffness: 200,
                }}
                viewport={{ once: true }}
                className="group"
              >
                <div
                  className="relative p-5 rounded-2xl transition-all duration-500 overflow-hidden cursor-pointer"
                  style={{
                    backgroundColor: 'var(--bg)',
                    border: '1px solid var(--border)',
                  }}
                >
                  {/* Hover gradient overlay */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background:
                        'linear-gradient(135deg, var(--text), transparent 70%)',
                      opacity: 0.02,
                    }}
                  />

                  {/* Animated progress bar at bottom */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-0.5 rounded-full"
                    style={{
                      backgroundColor: 'var(--text)',
                      opacity: 0.2,
                    }}
                    initial={{ width: '0%' }}
                    whileInView={{ width: '100%' }}
                    transition={{ duration: 0.8, delay: idx * 0.1 + 0.3 }}
                    viewport={{ once: true }}
                  />

                  <div className="relative z-10 text-center">
                    {/* Icon with animated ring */}
                    <div className="relative inline-block mb-3">
                      <motion.div
                        className="absolute inset-0 rounded-full"
                        animate={{
                          scale: [1, 1.3, 1],
                          opacity: [0.2, 0, 0.2],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          delay: idx * 0.3,
                        }}
                        style={{
                          border: '2px solid var(--text)',
                          opacity: 0.05,
                        }}
                      />
                      <motion.div
                        className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg mx-auto"
                        style={{
                          backgroundColor: 'var(--border)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                        }}
                        whileHover={{ rotate: [0, -15, 15, -10, 10, 0] }}
                        transition={{ duration: 0.6 }}
                      >
                        <benefit.icon
                          className="w-6 h-6 transition-all duration-300 group-hover:scale-110"
                          style={{ color: 'var(--text)' }}
                        />
                      </motion.div>
                    </div>

                    {/* Counter animation for title */}
                    <motion.h4
                      className="font-semibold text-sm mb-1"
                      style={{ color: 'var(--text)' }}
                    >
                      {benefit.title}
                    </motion.h4>

                    <motion.p
                      className="text-xs leading-relaxed"
                      style={{ color: 'var(--muted)' }}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: idx * 0.1 + 0.2 }}
                      viewport={{ once: true }}
                    >
                      {benefit.text}
                    </motion.p>

                    {/* Animated indicator dot */}
                    <motion.div
                      className="w-1.5 h-1.5 rounded-full mx-auto mt-3"
                      style={{
                        backgroundColor: 'var(--text)',
                        opacity: 0.2,
                      }}
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.2, 0.5, 0.2],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: idx * 0.2,
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <GlassyDivider />

        {/* Form Section */}
        <section className="animate-on-scroll py-20 px-4 max-w-6xl mx-auto transition-all duration-700 delay-200">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - Form */}
            <div className="relative">
              <div className="relative z-10">
                <span className="text-sm uppercase tracking-wider opacity-60 mb-4 block">
                  Get in Touch
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Send me a<br />
                  <span className="italic">message</span>
                </h2>

                {/* ==================== SPAM ALERT BANNER ==================== */}
                {showSpamAlert && (
                  <div
                    className="mb-6 p-3 rounded-lg border animate-fade-in"
                    style={{
                      backgroundColor: 'rgba(245, 158, 11, 0.06)',
                      borderColor: 'rgba(245, 158, 11, 0.2)',
                    }}
                  >
                    <div className="flex items-start gap-2">
                      <FiAlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-[11px] font-medium"
                          style={{ color: '#f59e0b' }}
                        >
                          📬 Check Your Spam Folder
                        </p>
                        <p
                          className="text-[10px] leading-relaxed"
                          style={{ color: 'var(--muted)' }}
                        >
                          Our response may go to spam. Check there if you don't
                          see our reply.
                        </p>
                        {/* Pro Tip with explanation */}
                        <div className="flex items-start gap-1.5 mt-1 p-1.5 rounded bg-blue-500/5 border border-blue-500/10">
                          <FiMail className="w-3 h-3 text-blue-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p
                              className="text-[10px]"
                              style={{ color: 'var(--muted)' }}
                            >
                              <span className="font-medium text-blue-500">
                                Pro tip:
                              </span>{' '}
                              Add{' '}
                              <span className="text-blue-500 font-mono text-[9px]">
                                wdhejbul@gmail.com
                              </span>{' '}
                              to your contacts — this tells your email provider
                              <span className="text-[9px] block mt-0.5 text-blue-400/70">
                                (Gmail, Outlook, etc.) that we're a trusted
                                sender, so our replies go straight to your
                                inbox, not spam.
                              </span>
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowSpamAlert(false)}
                          className="text-[9px] mt-1 hover:underline opacity-60"
                          style={{ color: 'var(--muted)' }}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Validation Progress */}
                {Object.keys(touched).length > 0 &&
                  validationProgress < 100 && (
                    <div className="mb-8">
                      <div
                        className="flex justify-between text-xs mb-2"
                        style={{ color: 'var(--muted)' }}
                      >
                        <span>Form completion</span>
                        <span>{Math.round(validationProgress)}%</span>
                      </div>
                      <div
                        className="h-1 rounded-full overflow-hidden"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${validationProgress}%`,
                            backgroundColor:
                              validationProgress === 100
                                ? '#10b981'
                                : 'var(--text)',
                          }}
                        />
                      </div>
                    </div>
                  )}

                {/* Success Message */}
                <AnimatePresence>
                  {isSubmitted && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="mb-6 p-5 rounded-2xl flex items-center gap-3"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
                      >
                        <FiCheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <p className="font-semibold">
                          Message Sent Successfully!
                        </p>
                        <p className="text-xs opacity-70">
                          I'll get back to you within 24 hours.
                        </p>
                        <p className="text-xs text-yellow-500 mt-1">
                          ⚠️ Please check your spam folder if you don't see my
                          reply!
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Error Message */}
                {submitError && (
                  <div
                    className="mb-6 p-5 rounded-2xl flex items-center gap-3"
                    style={{ backgroundColor: 'var(--border)' }}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                    >
                      <FiAlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="font-semibold">Something went wrong</p>
                      <p className="text-xs opacity-70">{submitError}</p>
                    </div>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Field */}
                  <div className="relative">
                    <div
                      className="absolute left-4 top-7.5 -translate-y-1/2"
                      style={{
                        color:
                          focusedField === 'name'
                            ? 'var(--text)'
                            : 'var(--muted)',
                      }}
                    >
                      <FiUser className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => {
                        setFocusedField(null);
                        handleBlur('name');
                      }}
                      placeholder="Your full name *"
                      className="w-full pl-11 pr-12 py-4 rounded-xl outline-none transition-all duration-300 focus:ring-1 focus:ring-current"
                      style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${errors.name ? '#ef4444' : getFieldStatus('name') === 'success' ? '#10b981' : 'var(--border)'}`,
                        color: 'var(--text)',
                      }}
                    />
                    {getFieldStatus('name') === 'success' && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <FiCheck className="w-4 h-4 text-green-500" />
                      </div>
                    )}
                    <div className="h-6">
                      {errors.name && touched.name && (
                        <div className="text-xs text-red-500 flex items-center gap-1">
                          <FiAlertCircle className="w-3 h-3" />
                          {errors.name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <label
                        className="text-sm font-medium"
                        style={{ color: 'var(--text)' }}
                      >
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <div
                        className="relative"
                        onMouseEnter={() => setShowEmailTooltip(true)}
                        onMouseLeave={() => setShowEmailTooltip(false)}
                      >
                        <FiInfo
                          className="w-3.5 h-3.5 cursor-help"
                          style={{ color: '#3b82f6' }}
                        />
                        {showEmailTooltip && (
                          <div
                            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 rounded-lg shadow-lg z-50 animate-fade-in"
                            style={{
                              backgroundColor: 'var(--bg)',
                              border: '1px solid var(--border)',
                              color: 'var(--text)',
                            }}
                          >
                            <div className="text-xs space-y-1">
                              <p className="font-semibold">
                                Why we need your email?
                              </p>
                              <p>• We'll send confirmation of your message</p>
                              <p>• Get updates about your inquiry</p>
                              <p>• Receive our response within 24 hours</p>
                              <div
                                className="mt-1 pt-1 border-t"
                                style={{ borderColor: 'var(--border)' }}
                              >
                                <p className="text-yellow-500 text-[10px]">
                                  ⚠️ Invalid emails won't receive confirmation
                                </p>
                              </div>
                            </div>
                            <div
                              className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 rotate-45"
                              style={{
                                backgroundColor: 'var(--bg)',
                                borderRight: '1px solid var(--border)',
                                borderBottom: '1px solid var(--border)',
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <EmailValidationInput
                      value={formData.email}
                      onChange={e => {
                        setFormData(prev => ({
                          ...prev,
                          email: e.target.value,
                        }));
                        if (errors.email) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.email;
                            return newErrors;
                          });
                        }
                      }}
                      onBlur={() =>
                        setTouched(prev => ({ ...prev, email: true }))
                      }
                      placeholder="you@example.com"
                      required
                      className="w-full"
                      onValidationChange={handleEmailValidationChange}
                    />
                  </div>

                  {/* Subject Toggle */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm" style={{ color: 'var(--muted)' }}>
                      Subject *
                    </span>
                    <button
                      type="button"
                      onClick={() => setUseCustomSubject(!useCustomSubject)}
                      className="text-xs flex items-center gap-1 transition-opacity hover:opacity-70"
                      style={{ color: 'var(--muted)' }}
                    >
                      {useCustomSubject ? (
                        <>
                          <FiMinus className="w-3 h-3" /> Use predefined subject
                        </>
                      ) : (
                        <>
                          <FiPlus className="w-3 h-3" /> Write custom subject
                        </>
                      )}
                    </button>
                  </div>

                  {/* Subject Field */}
                  {!useCustomSubject ? (
                    <div className="relative">
                      <div
                        className="absolute left-4 top-7.5 -translate-y-1/2 z-10"
                        style={{
                          color:
                            focusedField === 'subject'
                              ? 'var(--text)'
                              : 'var(--muted)',
                        }}
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </div>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('subject')}
                        onBlur={() => {
                          setFocusedField(null);
                          handleBlur('subject');
                        }}
                        className="w-full pl-11 pr-4 py-4 rounded-xl outline-none transition-all duration-300 appearance-none cursor-pointer focus:ring-1 focus:ring-current"
                        style={{
                          backgroundColor: 'transparent',
                          border: `1px solid ${errors.subject ? '#ef4444' : getFieldStatus('subject') === 'success' ? '#10b981' : 'var(--border)'}`,
                          color: 'var(--text)',
                        }}
                      >
                        <option
                          value=""
                          style={{ backgroundColor: 'var(--bg)' }}
                        >
                          Select a subject *
                        </option>
                        {Object.entries(groupedSubjects).map(
                          ([category, options]) => (
                            <optgroup
                              key={category}
                              label={category}
                              style={{
                                backgroundColor: 'var(--bg)',
                                color: 'var(--text)',
                              }}
                            >
                              {options.map(option => (
                                <option
                                  key={option.value}
                                  value={option.value}
                                  style={{ backgroundColor: 'var(--bg)' }}
                                >
                                  {option.label}
                                </option>
                              ))}
                            </optgroup>
                          ),
                        )}
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg
                          className="w-4 h-4"
                          style={{ color: 'var(--muted)' }}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                      <div className="h-6">
                        {errors.subject && touched.subject && (
                          <div className="text-xs text-red-500 flex items-center gap-1">
                            <FiAlertCircle className="w-3 h-3" />
                            {errors.subject}
                          </div>
                        )}
                      </div>
                      {formData.subject && !errors.subject && (
                        <div
                          className="mt-2 text-xs flex items-center gap-2 p-2 rounded"
                          style={{ backgroundColor: 'var(--border)' }}
                        >
                          <FiInfo className="w-3 h-3" />
                          <span style={{ color: 'var(--muted)' }}>
                            {subjectOptions.find(
                              opt => opt.value === formData.subject,
                            )?.description || 'Selected subject'}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative">
                      <div
                        className="absolute left-4 top-7.5 -translate-y-1/2"
                        style={{
                          color:
                            focusedField === 'customSubject'
                              ? 'var(--text)'
                              : 'var(--muted)',
                        }}
                      >
                        <FiType className="w-4 h-4" />
                      </div>
                      <input
                        type="text"
                        name="customSubject"
                        value={formData.customSubject}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('customSubject')}
                        onBlur={() => {
                          setFocusedField(null);
                          handleBlur('customSubject');
                        }}
                        placeholder="Enter your subject *"
                        className="w-full pl-11 pr-12 py-4 rounded-xl outline-none transition-all duration-300 focus:ring-1 focus:ring-current"
                        style={{
                          backgroundColor: 'transparent',
                          border: `1px solid ${errors.customSubject ? '#ef4444' : getFieldStatus('customSubject') === 'success' ? '#10b981' : 'var(--border)'}`,
                          color: 'var(--text)',
                        }}
                      />
                      {getFieldStatus('customSubject') === 'success' && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <FiCheck className="w-4 h-4 text-green-500" />
                        </div>
                      )}
                      <div className="h-6">
                        {errors.customSubject && touched.customSubject && (
                          <div className="text-xs text-red-500 flex items-center gap-1">
                            <FiAlertCircle className="w-3 h-3" />
                            {errors.customSubject}
                          </div>
                        )}
                      </div>
                      {touched.customSubject &&
                        formData.customSubject &&
                        !errors.customSubject && (
                          <div
                            className="mt-2 text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            <span>
                              Characters: {formData.customSubject.length}/100
                            </span>
                          </div>
                        )}
                    </div>
                  )}

                  {/* Message Field */}
                  <div className="relative">
                    <div
                      className="absolute left-4 top-5.5"
                      style={{
                        color:
                          focusedField === 'message'
                            ? 'var(--text)'
                            : 'var(--muted)',
                      }}
                    >
                      <FiMessageCircle className="w-4 h-4" />
                    </div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => {
                        setFocusedField(null);
                        handleBlur('message');
                      }}
                      placeholder="Tell me about your project... * (minimum 10 characters)"
                      rows="5"
                      className="w-full pl-11 pr-4 py-4 rounded-xl outline-none transition-all duration-300 resize-none focus:ring-1 focus:ring-current"
                      style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${errors.message ? '#ef4444' : getFieldStatus('message') === 'success' ? '#10b981' : 'var(--border)'}`,
                        color: 'var(--text)',
                      }}
                    />
                    {touched.message && (
                      <div
                        className="absolute bottom-4 right-4 text-xs px-2 py-1 rounded"
                        style={{
                          backgroundColor: 'var(--border)',
                          color:
                            formData.message.length >= 5000
                              ? '#ef4444'
                              : formData.message.length >= 4000
                                ? '#f59e0b'
                                : 'var(--muted)',
                        }}
                      >
                        {formData.message.length}/5000
                      </div>
                    )}
                    <div className="h-6">
                      {errors.message && touched.message && (
                        <div className="text-xs text-red-500 flex items-center gap-1">
                          <FiAlertCircle className="w-3 h-3" />
                          {errors.message}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting || !isFormValid}
                    whileHover={isFormValid ? { scale: 1.02 } : {}}
                    whileTap={isFormValid ? { scale: 0.98 } : {}}
                    className="relative w-full py-4 rounded-xl font-semibold overflow-hidden group transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: isFormValid
                        ? 'var(--text)'
                        : 'var(--border)',
                      color: isFormValid ? 'var(--bg)' : 'var(--muted)',
                    }}
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {isSubmitting ? (
                        <>
                          <FiLoader className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <FiSend className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                          Send Message
                        </>
                      )}
                    </span>
                  </motion.button>
                </form>

                {/* ==================== SOCIAL LINKS - Matching Footer ==================== */}
                <div
                  className="mt-8 pt-8"
                  style={{ borderTop: '1px solid var(--border)' }}
                >
                  <p className="text-sm opacity-60 mb-4">
                    Connect with me on social media
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {socialLinks.map((social, idx) => (
                      <motion.a
                        key={idx}
                        href={social.link}
                        className="p-3 rounded-xl transition-all duration-300"
                        style={{
                          backgroundColor: 'var(--border)',
                          color: 'var(--text)',
                        }}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <social.icon className="w-5 h-5" />
                      </motion.a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - FAQ & Info */}
            <div>
              {/* Quick Response Guarantee */}
              <GlassyCard className="mb-8 p-6 rounded-2xl text-center relative overflow-hidden">
                <motion.div
                  animate={{
                    scale: [1, 1.05, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl"
                  style={{
                    background:
                      'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                    opacity: 0.02,
                  }}
                />
                <div className="relative z-10">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: 'var(--bg)' }}
                  >
                    <FiClock
                      className="w-8 h-8"
                      style={{ color: 'var(--text)' }}
                    />
                  </div>
                  <h3
                    className="text-xl font-semibold mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Quick Response Guarantee
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--muted)' }}>
                    I personally respond to all messages within 24 hours during
                    business days.
                  </p>
                  <p className="text-xs text-yellow-500 mt-2">
                    ⚠️ Please check your spam folder if you don't see my reply!
                  </p>
                </div>
              </GlassyCard>

              {/* FAQ Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-6">
                  <FiSmile className="w-5 h-5" />
                  <h3
                    className="text-xl font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    Frequently Asked Questions
                  </h3>
                </div>
                <div className="space-y-4">
                  {faqs.map((faq, idx) => (
                    <GlassyCard
                      key={idx}
                      className="p-4 rounded-xl transition-all duration-300 hover:translate-x-1 cursor-pointer"
                    >
                      <h4
                        className="font-semibold mb-2"
                        style={{ color: 'var(--text)' }}
                      >
                        {faq.q}
                      </h4>
                      <p className="text-sm" style={{ color: 'var(--muted)' }}>
                        {faq.a}
                      </p>
                    </GlassyCard>
                  ))}
                </div>
              </div>

              {/* Interactive Map */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-6">
                  <HiOutlineLocationMarker className="w-5 h-5" />
                  <h3
                    className="text-xl font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    Find me here
                  </h3>
                </div>
                <GlassyCard className="relative rounded-2xl overflow-hidden group cursor-pointer">
                  <div className="aspect-video relative">
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      <div className="text-center">
                        <motion.div
                          animate={{
                            scale: [1, 1.1, 1],
                            opacity: [0.5, 1, 0.5],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          }}
                        >
                          <FiMapPin
                            className="w-8 h-8 mx-auto mb-2"
                            style={{ color: 'var(--text)' }}
                          />
                        </motion.div>
                        <p className="text-sm" style={{ color: 'var(--text)' }}>
                          Dhaka, Bangladesh
                        </p>
                        <p className="text-xs opacity-60 mt-2">
                          Available for remote work worldwide
                        </p>
                      </div>
                    </div>
                  </div>
                </GlassyCard>
              </div>

              {/* Email Notice */}
              <GlassyCard className="p-4 rounded-xl text-center">
                <p className="text-sm flex items-center justify-center gap-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span style={{ color: 'var(--text)' }}>
                    Please provide a valid email to receive my response
                  </span>
                </p>
                <p className="text-xs text-yellow-500 mt-2">
                  ⚠️ Check your spam folder if you don't see my reply!
                </p>
              </GlassyCard>
            </div>
          </div>
        </section>

        <GlassyDivider />

        {/* ==================== TRUSTED COMPANIES - Larger Height ==================== */}
        <section className="animate-on-scroll py-20 px-4 transition-all duration-700 delay-300 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiAward className="w-4 h-4" />
                <span className="text-sm" style={{ color: 'var(--text)' }}>
                  Trusted by industry leaders
                </span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-2xl md:text-3xl font-bold mb-4"
                style={{ color: 'var(--text)' }}
              >
                Trusted by creators worldwide
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-sm"
                style={{ color: 'var(--muted)' }}
              >
                Join thousands of satisfied clients who trust my work
              </motion.p>
            </div>

            {/* Larger height container */}
            <div className="py-8">
              <AnimatedTrustedCompanies />
            </div>
          </div>
        </section>

        <GlassyDivider />

        {/* ==================== FIVERR CARD - Using Reusable Component ==================== */}
        <section className="py-12 px-4 max-w-7xl mx-auto">
          <FiverrCard variant="standard" />
        </section>

        <GlassyDivider />

        {/* ==================== QUOTE SECTION ==================== */}
        <section className="animate-on-scroll py-20 px-4 max-w-4xl mx-auto text-center transition-all duration-700 delay-400">
          <GlassyCard className="p-12 rounded-3xl">
            <blockquote
              className="text-xl md:text-2xl font-light italic leading-relaxed"
              style={{ color: 'var(--text)' }}
            >
              "Great design is born from great communication. Let's start a
              conversation that leads to something amazing."
            </blockquote>
            <div className="mt-6 flex items-center justify-center gap-2">
              <FiArrowRight
                className="w-4 h-4"
                style={{ color: 'var(--muted)' }}
              />
              <span
                className="text-sm opacity-60"
                style={{ color: 'var(--muted)' }}
              >
                Your vision, my expertise
              </span>
            </div>
          </GlassyCard>
        </section>

        <style jsx>{`
          @keyframes shimmer {
            0% {
              transform: translateX(-100%) rotate(12deg);
            }
            100% {
              transform: translateX(200%) rotate(12deg);
            }
          }
          .animate-fade-in {
            animation: fadeIn 0.3s ease-out forwards;
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}</style>
      </div>
    </>
  );
}

export default Contact;
